import type { LineageHierarchyType, LineageNode } from '@/shared/types/lineage';

/**
 * 노드 집합에서 서비스 계층이 존재하는지 확인
 * 서비스 정보가 있는 노드가 하나라도 있으면 true 반환
 */
export const hasService = (nodes: Record<string, { entity: LineageNode['entity'] }>): boolean => {
  return Object.values(nodes).some((node) => node.entity.service);
};

/**
 * 노드 집합에서 도메인 계층이 존재하는지 확인
 * 도메인 정보가 있는 노드가 하나라도 있으면 true 반환
 */
export const hasDomain = (nodes: Record<string, { entity: LineageNode['entity'] }>): boolean => {
  return Object.values(nodes).some((node) => node.entity.domains && node.entity.domains.length > 0);
};

/**
 * 노드 집합에서 고유한 도메인 목록을 추출
 * 중복 제거된 도메인 정보를 id, name, displayName 형태로 반환
 * displayName이 없는 경우 name을 대체값으로 사용
 */
export const extractUniqueDomains = (
  nodes: Record<string, { entity: LineageNode['entity'] }>
): Array<{ id: string; name: string; displayName: string }> => {
  const domainsMap = new Map<string, { id: string; name: string; displayName: string }>();

  Object.values(nodes).forEach((node) => {
    const domains = node.entity.domains;
    if (domains && domains.length > 0) {
      domains.forEach((domain) => {
        if (!domainsMap.has(domain.id)) {
          domainsMap.set(domain.id, {
            id: domain.id,
            name: domain.name,
            displayName: domain.displayName || domain.name,
          });
        }
      });
    }
  });

  return Array.from(domainsMap.values());
};

/**
 * 노드 집합에서 컬럼 계층이 존재하는지 확인
 * 컬럼 정보가 있는 노드가 하나라도 있으면 true 반환
 */
export const hasColumns = (nodes: Record<string, { entity: LineageNode['entity'] }>): boolean => {
  return Object.values(nodes).some((node) => node.entity.columns && node.entity.columns.length > 0);
};

/**
 * 주어진 엔티티가 선택된 계층 필터와 일치하는지 확인
 * 선택된 계층이 없으면 true 반환
 * 선택된 계층이 있으면 해당 계층의 정보가 엔티티에 존재하는지 확인
 * service, domain, columns 계층 지원
 */
export const matchesHierarchyFilter = (
  entity: LineageNode['entity'],
  selectedHierarchies: Set<LineageHierarchyType>
): boolean => {
  if (selectedHierarchies.size === 0) {
    return true;
  }

  const hasMatchingHierarchy = Array.from(selectedHierarchies).some((hierarchy) => {
    switch (hierarchy) {
      case 'service':
        return !!entity.service;
      case 'domain':
        return entity.domains && entity.domains.length > 0;
      case 'columns':
        return entity.columns && entity.columns.length > 0;
      default:
        return false;
    }
  });

  return hasMatchingHierarchy;
};
